import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { DetailItemCardVariant } from './types'

import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ItemMenuOptions from 'tripPlanner/components/ItemCard/Common/ItemMenuOptions'
import SavedItemRemoveButton from 'tripPlanner/components/ItemCard/Common/SavedItemRemoveButton'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import MenuButton from 'tripPlanner/Tripkit/Core/MenuButton/MenuButton'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isCustomItem } from 'tripPlanner/utils'
import { WideCardOnlyBookedLabel } from './BookedLabel'
import useNavigateTo from 'hooks/useNavigateTo'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import SolidTimesIcon from 'components/Luxkit/Icons/solid/SolidTimesIcon'
import Clickable from 'components/Common/Clickable'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import * as Analytics from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { offerIdOrPlaceId } from 'tripPlanner/utils/analytics'

const FloatRight = styled.div`
  float: right;
  padding-left: ${rem(8)};
  padding-bottom: ${rem(4)};

  @media print {
    display: none;
  }
`

function ItineraryButtonWrapper({
  variant,
  children,
}: React.PropsWithChildren<{ variant: DetailItemCardVariant }>) {
  if (variant === 'drawer') {
    return null
  }
  if (variant === 'itinerary') {
    return <CSSBreakpoint min="tablet">{children}</CSSBreakpoint>
  }
  return <>{children}</>
}

interface Props {
  item: TripItem
  variant: DetailItemCardVariant
  offerLink?: string
}

function CornerControls({
  item,
  variant,
  offerLink,
}: Props) {
  const openModal = useOpenModal()

  const { setImmersiveMode } = useImmersiveModeHandlers()
  const navigateTo = useNavigateTo()

  const onClickAddTripItemDates = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: itemSectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const onRequestAddTripDates = useCallback(
    (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId: item.tripId, onDatesAdded })
    },
    [openModal, item.tripId],
  )

  const onClickDelete = useCallback(
    () => {
      const onDeleted = (item: TripItem) => {
        const analyticsId = offerIdOrPlaceId(item)
        if (analyticsId) {
          Analytics.trackClientEvent({
            subject: `remove_recommended_item_${analyticsId}`,
            action: 'concierge',
            type: 'interaction',
            category: AnalyticsPage.tripPlanner,
          })
        }
      }
      openModal(DeleteTripItem, { item, onDeleted })
    },
    [openModal, item],
  )

  const isBooked = 'isBooked' in item && item.isBooked

  return (
    <FloatRight>
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {item.isSuggested && <Clickable onClick={onClickDelete}>
          <SolidTimesIcon colour="neutral-three" />
        </Clickable>}
        {!item.isSuggested && <>
          {isBooked && <WideCardOnlyBookedLabel />}
          {isCustomItem(item) && (
            <>
              <ItineraryButtonWrapper variant={variant}>
                <ItineraryDateButton
                  item={item}
                  variant="chip"
                  onRequestAddTripDates={onRequestAddTripDates}
                />
              </ItineraryButtonWrapper>
              <MenuButton
                title=""
                bodyContent={<ItemMenuOptions item={item} />}
              />
            </>
          )}
          {!isCustomItem(item) && (
            <>
              <ItineraryButtonWrapper variant={variant}>
                <AddRemoveItineraryButton
                  item={item}
                  onClickAddTripItemDates={onClickAddTripItemDates}
                />
              </ItineraryButtonWrapper>
              <SavedItemRemoveButton item={item} />
            </>
          )}
        </>}
      </Group>
    </FloatRight>
  )
}

export default CornerControls
